import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Fingerprint, ScanFace, Trash2, Shield, AlertCircle, Plus } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { fmtDate } from '@/lib/dateFmt';
import { toast } from 'sonner';
import {
  isWebAuthnSupported,
  isPlatformAuthenticatorAvailable,
  registerPasskey,
  listPasskeys,
  deletePasskey,
} from '../lib/webauthn';

// --- pure helpers (extracted to keep the component slim) ---

const detectIsApple = () =>
  typeof navigator !== 'undefined' && /iPhone|iPad|iPod|Mac/.test(navigator.userAgent);

const passkeyRegisterErrorMessage = (e) => {
  if (e?.name === 'NotAllowedError') return 'Registrazione annullata';
  const detail = e?.response?.data?.detail;
  if (detail) return typeof detail === 'string' ? detail : 'Errore registrazione';
  return e?.message || 'Errore registrazione';
};

// --- sub-views (one per state) ---

function PasskeyLoading() {
  return (
    <Card className="border-slate-200">
      <CardContent className="py-6 text-center">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-900 mx-auto" />
      </CardContent>
    </Card>
  );
}

function PasskeyUnsupported({ compact, biometricName, isApple }) {
  return (
    <Card className="border-slate-200" data-testid="passkey-not-supported">
      <CardHeader className={compact ? 'pb-2' : ''}>
        <CardTitle className="text-base flex items-center gap-2">
          <Shield className="h-4 w-4" />
          Sicurezza
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-start gap-2 text-sm text-slate-600 bg-slate-50 p-3 rounded-md">
          <AlertCircle className="h-4 w-4 mt-0.5 text-slate-400 shrink-0" />
          <div>
            {biometricName} non disponibile su questo dispositivo o browser.
            {isApple && ' Su iPhone usa Safari oppure aggiungi l\'app alla schermata Home.'}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function PasskeyRow({ cred, BiometricIcon, onRemove }) {
  return (
    <div
      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100"
      data-testid={`passkey-item-${cred.id}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="p-2 bg-blue-100 rounded-md">
          <BiometricIcon className="h-4 w-4 text-blue-600" />
        </div>
        <div className="min-w-0">
          <p className="font-medium text-sm text-slate-900 truncate">{cred.device_name}</p>
          <p className="text-xs text-slate-500">
            Aggiunto il {fmtDate(cred.created_at, 'dd MMM yyyy')}
            {cred.last_used_at && ` · Ultimo uso ${fmtDate(cred.last_used_at, 'dd MMM HH:mm')}`}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {!cred.last_used_at && (
          <Badge variant="outline" className="text-blue-600 border-blue-200 bg-blue-50 text-[10px]">
            Nuovo
          </Badge>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
          onClick={() => onRemove(cred)}
          data-testid={`remove-passkey-${cred.id}`}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

// --- main component ---

export default function PasskeyManager({ compact = false }) {
  const [supported, setSupported] = useState(false);
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);

  const isApple = detectIsApple();
  const BiometricIcon = isApple ? ScanFace : Fingerprint;
  const biometricName = isApple ? 'Face ID / Touch ID' : 'Biometria';

  const fetchPasskeys = useCallback(async () => {
    try {
      const data = await listPasskeys();
      setCredentials(data || []);
    } catch (e) {
      console.error('Errore caricamento passkeys:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isWebAuthnSupported()) {
        if (!cancelled) { setSupported(false); setLoading(false); }
        return;
      }
      const ok = await isPlatformAuthenticatorAvailable();
      if (cancelled) return;
      setSupported(ok);
      if (ok) await fetchPasskeys();
      else setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [fetchPasskeys]);

  const handleRegister = async () => {
    setRegistering(true);
    try {
      const defaultName = isApple ? 'iPhone / Mac' : 'Questo dispositivo';
      const name = window.prompt('Nome del dispositivo (es. iPhone Federico)', defaultName) || defaultName;
      await registerPasskey(name);
      toast.success(`${biometricName} attivato!`);
      await fetchPasskeys();
    } catch (e) {
      toast.error(passkeyRegisterErrorMessage(e));
    } finally {
      setRegistering(false);
    }
  };

  const handleRemove = async (cred) => {
    if (!window.confirm(`Rimuovere "${cred.device_name}"?`)) return;
    try {
      await deletePasskey(cred.id);
      toast.success('Passkey rimossa');
      await fetchPasskeys();
    } catch (err) {
      console.error('Errore rimozione passkey:', err);
      toast.error('Errore nella rimozione');
    }
  };

  if (loading) return <PasskeyLoading />;
  if (!supported) return <PasskeyUnsupported compact={compact} biometricName={biometricName} isApple={isApple} />;

  return (
    <Card className="border-slate-200" data-testid="passkey-manager">
      <CardHeader className={compact ? 'pb-2' : ''}>
        <CardTitle className="text-base flex items-center gap-2">
          <BiometricIcon className="h-4 w-4 text-blue-600" />
          {biometricName}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {credentials.length === 0 ? (
          <div className="text-sm text-slate-500">
            Attiva l'accesso biometrico per entrare con un tap, senza password.
          </div>
        ) : (
          <div className="space-y-2">
            {credentials.map((c) => (
              <PasskeyRow key={c.id} cred={c} BiometricIcon={BiometricIcon} onRemove={handleRemove} />
            ))}
          </div>
        )}
        <Button
          onClick={handleRegister}
          disabled={registering}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white"
          data-testid="register-passkey-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          {registering ? 'Registrazione...' : `Aggiungi ${biometricName}`}
        </Button>
      </CardContent>
    </Card>
  );
}
